import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormTextField } from 'components/common';
import { ApplicationFormPageEnum } from '../types';
import { useNavigateBack } from 'hooks';
import { applicationFormApi } from 'api/applicationForm';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';
import { getCurrentRoute } from 'utils/location';
import { IAdditionalNotesForm } from './types';
import { additionalNotesQuestions } from './questions';

export const AdditionalNotes = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);

  const form = useForm<IAdditionalNotesForm>({
    defaultValues: {
      additionalNotes: '',
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IAdditionalNotesForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: additionalNotesQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.REVIEW_AND_SUBMIT);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={additionalNotesQuestions.additionalNotes.explanationKey} tagName={'h4'} />
          <FormTextField name={'additionalNotes'} textFieldProps={{ multiline: true }} />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
